import { AttachedFiles } from "legacyComponents/FileUploadContainer.types";
import moment from "moment";

import type { AddScopeOfWorkLineItemsRequest } from "@btnet/services-btnet/handlers/models/addScopeOfWorkLineItemsRequest";
import type { CostGroup } from "@btnet/services-btnet/handlers/models/costGroup";
import { JobTaxVersion } from "@btnet/services-btnet/handlers/models/jobTaxVersion";
import type { LineItem } from "@btnet/services-btnet/handlers/models/lineItem";
import type { ProposalDisplayColumnTypes } from "@btnet/services-btnet/handlers/models/proposalDisplayColumnTypes";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import {
    type AccountingIntegrationType,
    type JobStatusTypes,
    type ProposalStatus,
} from "types/enum";

import { ListMetadata } from "utilities/list/list.types";

import { type RevisedCostsDisplayColumnTypes } from "commonComponents/utilities/LineItemDisplaySettings/LineItemDisplaySettings.api.types";

import { type IAssemblySelectorExtraData } from "entity/assembly/AssemblySelector/AssemblySelector";
import { type AssignToAssemblyActions } from "entity/assembly/AssignToAssembly/AssignToAssembly.api.types";
import {
    type IAddCostGroup,
    type IAddFromCatalogRequest,
} from "entity/costCatalog/CostCatalog.types";
import { type AdjustPercentMarkupActions } from "entity/estimate/AdjustPercentMarkup/AdjustPercentMarkup.api.types";
import {
    DisplayPreferences,
    ProposalCategory,
    TakeoffPartner,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import { ProposalLineItemSaveRequest } from "entity/estimate/EstimateLineItemContainer/EstimateLineItemContainer.types";
import { type ICostTypesMarkup } from "entity/job/Job.api.types";
import { LeadProposalResponse } from "entity/lead/Lead/Lead.api.types";
import { type ITaxGroup } from "entity/tax/common/tax.types";
import { type ICanSendToCartEntity } from "entity/vendor/common/vendor.types";

import type { default as PreviousDocsAPIResponse } from "./json/estimate.previousDocs.api.json";
import type { default as RelatedAllowanceAmountAPIResponse } from "./json/RelatedAllowanceAmountAPIResponse.api.json";

/** Narrowed off the untyped response so these reads don't add to this file's `any` debt. */
interface IInvoicedAmounts {
    alreadyInvoicedAmount?: number | null;
    remainingContractPrice?: number | null;
    hasInvoiceableLines?: boolean | null;
}

export class EstimateResponse implements ICanSendToCartEntity {
    constructor(data: any) {
        this.canAddAllowances = data.canAddAllowances;
        this.canAddSelections = data.canAddSelections;
        this.canUpdateOwnerInvoices = data.canUpdateOwnerInvoices;
        this.canEditBudget = data.canEditBudget;
        this.canSendToHomeDepotCart = data.canSendToHomeDepotCart;
        this.canLaunchTakeoff = data.canLaunchTakeoff;
        this.hasTakeoffUser = data.hasTakeoffUser;
        this.projectConnectedToTakeoffs = data.projectConnectedToTakeoffs;
        this.takeoffPartnerId = data.takeoffPartnerId;
        this.takeoffProjectLink = data.takeoffProjectLink ?? undefined;
        this.takeoffProjectName = data.takeoffProjectName;
        this.worksheetId = data.worksheetId;
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        const { externalId, externalBuilderId, externalJobId, createdBy, createdByDate } = data as {
            externalId?: string;
            externalBuilderId?: string;
            externalJobId?: string;
            createdBy?: string;
            createdByDate?: string;
        };
        this.externalId = externalId;
        this.externalBuilderId = externalBuilderId;
        this.externalJobId = externalJobId;
        this.createdBy = createdBy;
        this.createdByDate = createdByDate ? new Date(createdByDate) : undefined;
        this.isTemplate = data.isTemplate;
        this.worksheetDisplayPreferences = new DisplayPreferences<ProposalDisplayColumnTypes>(
            data.worksheetDisplayPreferences,
        );
        this.revisedCostsDisplayPreferences =
            new DisplayPreferences<RevisedCostsDisplayColumnTypes>(
                data.revisedCostsDisplayPreferences,
            );
        this.accountingIntegrationType = data.accountingIntegrationType;
        this.costCategories = data.costCategories.map(
            (category: any) => new BTSelectItem({ id: category.id, name: category.name }),
        );
        this.costCodes = data.costCodes.map(
            (code: any) =>
                new BTSelectItem<CostCodeExtraData>({
                    id: code.id,
                    name: code.name,
                    extraData: {
                        costCategoryId: code.costCategoryId,
                        isFlatRateCostCode: code.isFlatRateCostCode,
                    },
                }),
        );

        this.formatData = data.formatData
            ? data.formatData.map((category: any) => {
                  return new ProposalCategory(
                      category,
                      this.builderId,
                      this.jobId,
                      undefined,
                      false,
                  );
              })
            : [];

        this.revisedFormatData = data.revisedFormatData
            ? data.revisedFormatData.map((category: any) => {
                  return new ProposalCategory(
                      category,
                      this.builderId,
                      this.jobId,
                      undefined,
                      false,
                  );
              })
            : [];

        this.worksheetLocked = data.worksheetLocked;
        this.isManuallyLocked = data.isManuallyLocked;
        this.isSentToBudget = data.isSentToBudget;
        this.showRevisedCosts = data.showRevisedCosts;
        this.proposalStatus = data.proposalStatus;
        this.lockedByName = data.lockedByName;
        this.lockedByDate = data.lockedByDate && moment(data.lockedByDate);
        this.contractPrice = data.contractPrice;
        this.markupMarginPercentType = data.markupMarginPercentType;
        this.hasSingleSelectCostTypes = data.hasSingleSelectCostTypes;

        this.defaultMargin = data.defaultMargin;
        this.defaultMarkup = data.defaultMarkup;
        if (data.defaultTaxGroup) {
            this.defaultTaxGroup = data.defaultTaxGroup;
        }
        this.effectiveTaxVersion = data.effectiveTaxVersion ?? JobTaxVersion.None;
        if (data.costTypesMarkup) {
            this.costTypesMarkup = data.costTypesMarkup;
        } else {
            this.costTypesMarkup = [];
        }
        this.entityAddedDate = moment(data.entityAddedDate);
        this.hasJobProposalFeature = data.hasJobProposalFeature;
        this.costItemUpdateMessage = data.costItemUpdateMessage;
        this.connectedTakeoffPartners = data.connectedTakeoffPartners?.map(
            (tp: any) => new TakeoffPartner(tp),
        );

        this.assemblies = data.assemblies
            ? data.assemblies.map(
                  (x: any) =>
                      new BTSelectItem<IAssemblySelectorExtraData>({
                          id: x.id,
                          name: x.name,
                          extraData: x.extraData,
                      }),
              )
            : [];
        this.revisedCostAssemblies = data.revisedCostAssemblies
            ? data.revisedCostAssemblies.map(
                  (x: any) =>
                      new BTSelectItem<IAssemblySelectorExtraData>({
                          id: x.id,
                          name: x.name,
                      }),
              )
            : [];
        this.isCostPlusJob = data.isCostPlusJob;
        this.isJobLinkedToAccounting = data.isJobLinkedToAccounting;
        this.numberOfDraws = data.numberOfDraws;
        this.alreadyInvoicedAmount = (data as IInvoicedAmounts).alreadyInvoicedAmount;
        this.remainingContractPrice = (data as IInvoicedAmounts).remainingContractPrice;
        this.hasInvoiceableLines = (data as IInvoicedAmounts).hasInvoiceableLines;
        this.proposalTitle = data.proposalTitle;
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob;
    }

    canAddAllowances: boolean;
    canAddSelections: boolean;
    canUpdateOwnerInvoices: boolean;
    canEditBudget: boolean;
    canSendToHomeDepotCart: boolean;

    canLaunchTakeoff: boolean;
    hasTakeoffUser: boolean;
    projectConnectedToTakeoffs: boolean;
    takeoffPartnerId?: number;
    takeoffProjectLink?: string;
    takeoffProjectName?: string;

    worksheetId: number;
    builderId: number;
    jobId: number;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    createdBy?: string;
    createdByDate?: Date;
    isTemplate: boolean;
    worksheetDisplayPreferences: DisplayPreferences<ProposalDisplayColumnTypes>;
    revisedCostsDisplayPreferences: DisplayPreferences<RevisedCostsDisplayColumnTypes>;
    accountingIntegrationType: AccountingIntegrationType;

    /**
     * This indicates if the worksheet is manually locked or is sent to the budget.
     */
    worksheetLocked: boolean;
    /**
     * This indicates if the worksheet is manually locked.
     */
    isManuallyLocked: boolean;
    /**
     * This indicates if the worksheet is sent to the budget.
     */
    isSentToBudget: boolean;
    showRevisedCosts: boolean;
    proposalStatus: ProposalStatus;

    lockedByName: string;
    lockedByDate?: moment.Moment;
    entityAddedDate: moment.Moment;
    contractPrice: number;
    markupMarginPercentType: number;
    defaultMarkup?: number;
    defaultMargin?: number;
    defaultTaxGroup?: ITaxGroup;
    effectiveTaxVersion: JobTaxVersion;
    hasSingleSelectCostTypes: boolean;
    costTypesMarkup: ICostTypesMarkup[];

    formatData: ProposalCategory[];
    revisedFormatData: ProposalCategory[];
    costCategories: BTSelectItem[];
    costCodes: BTSelectItem<CostCodeExtraData>[];

    hasJobProposalFeature: boolean;
    costItemUpdateMessage: string | null;
    connectedTakeoffPartners: TakeoffPartner[] | undefined;
    assemblies: BTSelectItem<IAssemblySelectorExtraData>[];
    revisedCostAssemblies: BTSelectItem<IAssemblySelectorExtraData>[];
    isCostPlusJob: boolean;
    isJobLinkedToAccounting: boolean;

    numberOfDraws?: number;
    /** Portion of the estimate the job's existing invoices already cover. Null when the flag is off. */
    alreadyInvoicedAmount?: number | null;
    /** What the draws generated on send to budget will total. Zero means nothing will be created. */
    remainingContractPrice?: number | null;
    /** Whether the draws will create any invoices. False means every line is covered; null when the flag is off. */
    hasInvoiceableLines?: boolean | null;
    proposalTitle: string;
    isSelectionsReimaginedJob: boolean;
}

export class CostCodeExtraData {
    costCategoryId: number;
    isFlatRateCostCode?: boolean;
}

/**
 * The builder's flat-rate cost code id, or `null` when the builder has none. The fallback default
 * for new inline line items outside a category group.
 */
export function getFlatRateCostCodeId(costCodes: BTSelectItem<CostCodeExtraData>[]): number | null {
    const flatRate = costCodes.find((c) => c.extraData?.isFlatRateCostCode);
    return flatRate?.value == null ? null : Number(flatRate.value);
}

export class WorksheetLineItemSaveRequest {
    constructor(
        lineItem: WorksheetLineItem,
        taxMethod?: number,
        isReplacingFromCatalog: boolean = false,
        worksheetId?: number,
    ) {
        this.lineItem = new ProposalLineItemSaveRequest(lineItem);
        this.taxMethod = taxMethod;
        this.isReplacingFromCatalog = isReplacingFromCatalog;
        this.worksheetId = worksheetId;
    }

    lineItem: ProposalLineItemSaveRequest;
    taxMethod?: number;
    isReplacingFromCatalog: boolean;
    worksheetId?: number;
}

/**
 * Builds the v2 {@link AddScopeOfWorkLineItemsRequest} directly from worksheet line items.
 * Line items are normalized through {@link ProposalLineItemSaveRequest} (which renames the
 * front-end `WorksheetLineItem` fields to the API shape, e.g. `itemTitle` -> `title`,
 * `costItemId` -> `costCodeItemId`) before being mapped to the generated {@link LineItem} type.
 */
export function buildAddScopeOfWorkLineItemsRequest(
    lineItems: WorksheetLineItem[],
    worksheetId: number,
    options?: {
        addFromCatalogRequest?: IAddFromCatalogRequest;
        useDisplayOrders?: boolean;
        /** Top-level group to nest imported cost groups under. */
        costGroupParentId?: number;
    },
): AddScopeOfWorkLineItemsRequest {
    const { addFromCatalogRequest, useDisplayOrders, costGroupParentId } = options ?? {};
    return {
        parentId: worksheetId,
        includeDescription: addFromCatalogRequest?.includeDescription,
        includeFiles: addFromCatalogRequest?.includeFiles,
        includeSpecs: addFromCatalogRequest?.includeSpecs,
        lineItems: lineItems.map((li) =>
            mapProposalLineItemSaveRequestToLineItem(
                new ProposalLineItemSaveRequest(li, useDisplayOrders ? li.displayOrder : null),
            ),
        ),
        costGroups: addFromCatalogRequest?.costGroups?.map(mapAddCostGroupToCostGroup),
        costGroupParentId,
    };
}

export function mapProposalLineItemSaveRequestToLineItem(
    lineItem: ProposalLineItemSaveRequest,
): LineItem {
    return {
        id: lineItem.id,
        // `LineItemType`/`CostTypes`/`MarkedAs` (front-end enums) and the generated
        // `LineItemType`/`CostTypes`/`LineItemMarkedAs` are the same underlying enums under
        // different names; cast just these fields.
        pageTypeEnum: lineItem.pageTypeEnum as LineItem["pageTypeEnum"],
        costCode: lineItem.costCode,
        costCodeItemId: lineItem.costCodeItemId,
        title: lineItem.title,
        costTypes: lineItem.costTypes as LineItem["costTypes"],
        markedAs: lineItem.markedAs as LineItem["markedAs"],
        unitCost: lineItem.unitCost,
        unitType: lineItem.unitType,
        quantity: lineItem.quantity,
        builderCost: lineItem.builderCost,
        markupType: lineItem.markupType,
        markupPercent: lineItem.markupPercent,
        markupPerUnit: lineItem.markupPerUnit,
        markupAmount: lineItem.markupAmount,
        margin: lineItem.margin,
        ownerPrice: lineItem.ownerPrice,
        description: lineItem.description,
        internalNotes: lineItem.internalNotes,
        includeInCatalog: lineItem.includeInCatalog,
        costCategoryId: lineItem.costCategoryId,
        parentId: lineItem.parentId,
        purchaseOrderLineItemId: lineItem.purchaseOrderLineItemId,
        assemblyId: lineItem.assemblyId,
        importedFromProposalFormatItemId: lineItem.importedFromProposalFormatItemId,
        lineItemDisplayOrder: lineItem.lineItemDisplayOrder,
        taxGroupId: lineItem.taxGroupId,
    };
}

export function mapAddCostGroupToCostGroup(costGroup: IAddCostGroup): CostGroup {
    // The add endpoint only reads costGroupId/title/description off cost groups (see
    // ProposalFormatItemsEngine.CreateFormatItemsForEstimatesAsync). `files` and the remaining
    // CostGroup fields are intentionally omitted — they are not consumed, and `files` cannot be
    // mapped safely anyway (front-end `AttachedFiles` uses Moment dates, the generated type uses
    // Date).
    return {
        costGroupId: costGroup.costGroupId,
        title: costGroup.title,
        description: costGroup.description,
    };
}

export class LineItemUpdateResponseLegacy {
    constructor(data: any) {
        this.costItemId = data.costItemId;
        this.costItemTitle = data.costItemTitle;
        this.costItemLinkBroken = data.costItemLinkBroken;
    }
    costItemId: number | null;
    costItemTitle: string | null;
    costItemLinkBroken: boolean;
}

export class ProposalGroupSaveResponse {
    constructor(data: { groupId: number; externalId?: string | null; attachments?: unknown }) {
        this.groupId = data.groupId;
        this.externalId = data.externalId ?? undefined;
        // Same shape the worksheet load hands ProposalCategory; null when the group couldn't be reloaded.
        this.attachedFiles = data.attachments ? new AttachedFiles(data.attachments) : undefined;
    }
    groupId: number;
    /** Server-minted id; lets the new group link specs without a worksheet refetch. */
    externalId: string | undefined;
    /** The new group's attachments and the user's file permissions. */
    attachedFiles: AttachedFiles | undefined;
}

export class PreviousDocumentsTabResponse {
    constructor(data: typeof PreviousDocsAPIResponse) {
        if (data.proposalHistoricalDocs !== undefined) {
            this.proposalHistoricalDocs = data.proposalHistoricalDocs.map(
                (x: (typeof PreviousDocsAPIResponse.proposalHistoricalDocs)[0]) =>
                    new PreviousDocumentItem(x),
            );
        }
        if (data.proposalDashboardItems !== undefined) {
            this.proposalDashboardItems = data.proposalDashboardItems.map(
                (x: (typeof PreviousDocsAPIResponse.proposalDashboardItems)[0]) =>
                    new LeadProposalResponse(x),
            );
        }
        if (data.listMetadata) {
            this.listMetadata = new ListMetadata(data.listMetadata);
        }
    }

    proposalDashboardItems: LeadProposalResponse[];
    proposalHistoricalDocs: PreviousDocumentItem[];
    listMetadata?: ListMetadata;
}

export class PreviousDocumentItem {
    constructor(data: any) {
        this.fileName = data.Name;
        this.ownerPrice = data.OwnerPriceRaw;
        this.resetDate = moment(data.ResetDateRaw);
        this.statusUpdatedBy = data.StatusUpdatedBy === "" ? null : data.StatusUpdatedBy; // If it is from before we were stamping it, set it to null so we handle it correctly on the front-end
        this.status = data.StatusValue;
        this.statusUpdatedDate = moment(data.RawStatusUpdatedDate);
        this.fileLink = data.FileLink;
        this.jobId = data.JobId;
        this.proposalId = data.ProposalId;
        this.jobStatus = data.JobStatus;
    }

    fileName: string;
    ownerPrice: number; // The service already has this formatted
    resetDate: moment.Moment;
    statusUpdatedDate: moment.Moment;
    status: ProposalStatus;
    statusUpdatedBy: string | null;
    fileLink: string;
    jobId: number;
    proposalId: number;
    jobStatus: JobStatusTypes;
}

export class RelatedAllowanceAmountResponse {
    constructor(data: typeof RelatedAllowanceAmountAPIResponse) {
        this.customInvoiceId = data.amountInvoiced?.customInvoiceId;
        this.amountInvoiced = data.amountInvoiced?.amountInvoiced;
    }

    customInvoiceId?: string;
    amountInvoiced?: number;
}

export class CostCategoryAndId {
    constructor(categoryId: number, costCodeId: number) {
        this.categoryId = categoryId;
        this.costCodeId = costCodeId;
    }
    categoryId: number;
    costCodeId: number;
}

export type EstimateDisplayPreferences =
    | { kind: "worksheet"; prefs: DisplayPreferences<ProposalDisplayColumnTypes> }
    | { kind: "revisedCosts"; prefs: DisplayPreferences<RevisedCostsDisplayColumnTypes> };

export type EstimateActions =
    | "excelExport"
    | "newInvoice"
    | "formatAndShare"
    | "lockUnlockEstimate"
    | "saveLineItem"
    | "saveAssembly"
    | "deleteAssembly"
    | "addFromCostCode"
    | "checkedActions"
    | "saveColumnResize"
    | "sendToBudget"
    | "updateTaxableStatus"
    | AdjustPercentMarkupActions
    | AssignToAssemblyActions
    | undefined;

export enum EstimateFilterTypes {
    CostItems = "1",
    CostType = "2",
    MarkedAs = "3",
}

export enum TabKeys {
    Worksheet = "1",
    RevisedCosts = "2",
    Previous = "3",
}

export interface IEstimateLineItemModalConfiguration {
    parentRoute: string;
    beforeClose: (shouldReload?: boolean) => void;
}

export class WorksheetTaxGroupUpdateResponse {
    constructor(data: any) {
        this.worksheetLineItems = data.worksheetLineItems;
    }

    worksheetLineItems: WorksheetLineItem[];
}

// Display name passed as the cost catalog "add to entity" target when importing into an estimate worksheet.
export const estimateEntityName = "Line Item";
